import {
  AfterContentInit,
  Component,
  ElementRef,
  Input,
  ViewChild
} from '@angular/core';
import _ from 'lodash';
import { colors } from '../../../gameday/colors';
import { Selection, scaleLinear, select } from 'd3';

type SummaryBarSelection = Selection<HTMLElement, SummaryBar, any, any>;

const ANIMATION_DURATION = 500,
  BAR_MAX_WIDTH = 30,
  BAR_MIN_WIDTH = 2,
  INIT_ATTEMPTS = 4,
  INIT_DELAY = 250,
  PADDING_TOP = 10;

interface BarChartSize {
  width: number;
  padding: number;
}

function barWidth(chartWidth: number, barsAmount: number): BarChartSize {
  const frac = chartWidth / (barsAmount * 4 - 1);
  let width = frac * 3;
  let padding = frac;

  if (width > BAR_MAX_WIDTH) {
    width = BAR_MAX_WIDTH;
    padding = width / 3;
  } else if (width < BAR_MIN_WIDTH) {
    width = BAR_MIN_WIDTH;
    padding = width / 3;
  }

  return { width, padding };
}

interface SummaryBar {
  size: number;
  value: number;
  id: string;
  highlighted: boolean;
}

@Component({
  selector: 'muni-gameday-order-summary-bar-chart',
  template: '<svg #svg></svg>',
  styles: [
    `
      svg {
        width: 100%;
        height: 100%;
      }
    `
  ],
  standalone: false,
})
class OrderSummaryBarChartComponent implements AfterContentInit {
  @ViewChild('svg')
  svgRef: ElementRef;

  private width: number;
  private height: number;
  private barWidth: BarChartSize;
  private y;
  private svg;

  private _bars: SummaryBar[];

  private initAttempts = 0;

  @Input()
  set bars(bars: SummaryBar[]) {
    this._bars = bars || [];
    this.render();
  }

  get bars(): SummaryBar[] {
    return this._bars;
  }

  ngAfterContentInit() {
    this.render();
  }

  private x(i: number): string {
    let x = i * this.barWidth.width;
    if (this.barWidth.width === BAR_MAX_WIDTH) {
      x +=
        (this.width -
          (this.barWidth.width + this.barWidth.padding) * this.bars.length +
          this.barWidth.padding) /
        2;
    }
    return `translate(${x},0)`;
  }

  private rect(
    selection: SummaryBarSelection,
    key: string,
    color: string,
    highlightColor = color
  ) {
    return selection
      .attr('x', (d, i) => this.barWidth.padding * i)
      .attr('y', (d) => this.y(d[key]))
      .attr('height', (d) => this.height - this.y(d[key]))
      .attr('width', this.barWidth.width)
      .attr('fill', (d) => (d.highlighted ? highlightColor : color))
      .attr('fill-opacity', 1);
  }

  private sizeRect(selection: SummaryBarSelection): SummaryBarSelection {
    return this.rect(
      selection.attr('data-automation-e2e-selector', 'sizeRect'),
      'size',
      colors.special.expected
    );
  }

  private valueRect(selection: SummaryBarSelection): SummaryBarSelection {
    return this.rect(
      selection.attr('data-automation-e2e-selector', 'valueRect'),
      'value',
      colors.blue.primary,
      colors.special.highlight
    );
  }

  private highlightRect(selection: SummaryBarSelection): SummaryBarSelection {
    return selection
      .attr(
        'x',
        (d, i) => this.barWidth.padding * i - this.barWidth.padding / 2
      )
      .attr('y', 0)
      .attr('height', (d) => this.height)
      .attr('width', this.barWidth.width + this.barWidth.padding)
      .attr('fill', colors.special.highlightBg)
      .attr('fill-opacity', (d) => (d.highlighted ? 0.1 : 0));
  }

  private init() {
    const nativeSVG: SVGSVGElement = this.svgRef.nativeElement;
    this.svg = this.svg || select(nativeSVG);

    const rect = nativeSVG.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
  }

  private render() {
    if (!this.ready()) {
      if (this.initAttempts > INIT_ATTEMPTS) {
        throw new Error("OrderSummaryBarChartComponent can't init");
      }
      setTimeout(() => {
        this.initAttempts++;
        this.init();
        this.render();
      }, INIT_DELAY);
      return;
    }

    this.y = scaleLinear()
      .range([this.height, PADDING_TOP])
      .domain([
        0,
        _.max(this.bars.map((bar) => Math.max(bar.value, bar.size)))
      ]);

    this.init();
    this.barWidth = barWidth(this.width, this.bars.length);

    const bars = this.svg.selectAll('g').data(this.bars);

    const newBars = bars
      .enter()
      .append('g')
      .attr('transform', (d, i) => this.x(i))
      .attr('auto-maturity', (d: SummaryBar) => d.id)
      .attr('auto-maturity-size', (d: SummaryBar) => d.size)
      .attr('data-automation-e2e-selector', 'miniChart');

    bars
      .transition()
      .duration(ANIMATION_DURATION)
      .attr('transform', (d, i) => this.x(i));

    this.highlightRect(newBars)
      .append('rect')
      .attr('class', 'highlight')
      .attr('data-automation-e2e-selector', 'highlightRect');
    this.highlightRect(bars.select('.highlight'));

    this.sizeRect(newBars.append('rect').attr('class', 'size'));
    this.sizeRect(
      bars.select('.size').transition().duration(ANIMATION_DURATION) as any
    );

    this.valueRect(newBars.append('rect').attr('class', 'value'));
    this.valueRect(
      bars.select('.value')
      //.transition().duration(ANIMATION_DURATION) as any
    );

    bars.exit().remove();
  }

  private ready() {
    return !!this.bars && !!this.svg && this.height > 0 && this.width > 0;
  }
}

export { SummaryBar, OrderSummaryBarChartComponent };
